import { useState } from "react";
import { storyPageContent } from "../data/services";
import { motion } from "framer-motion";
import ServicesBanner from "../components/common/ServicesBanner";
import userImg from "../assets/icons/CLIENTImg.png";
import OpportunitiesImg from "../assets/icons/OPPORTUNITIESImg.png";
import ChallengesImg from "../assets/icons/CHALLENEGS.jpg";
import SuccessImg from "../assets/icons/SUCCESS.jpg";
import erpStyles from "../pages/styles/ERPSolutions.module.css";

// ─── All 3 case studies data ───────────────────────────────────────────────
const caseStudies = {
  rifab: {
    label: "Customized CRM & LMS for a Fabrication Company",
    cards: [
      {
        title: "Client Background",
        img: userImg,
        description:
          "Fabrication company from Pune designs and delivers prefabricated homes, offices, and PODs - a designer-led product range built to bring client's visions to life, faster and with more flexibility than traditional construction.",
      },
      {
        title: "Opportunity",
        img: OpportunitiesImg,
        description:
          "With increasing projects and teams, the company sought to centralize customer and project management while standardizing employee training to improve collaboration, operational efficiency, and scalability through an integrated CRM and LMS.",
      },
      {
        title: "Challenge",
        img: ChallengesImg,
        description:
          "Client and project tracking scattered across spreadsheets and inboxes, with no shared visibility for sales, design, and delivery teams  compounded by an unstructured training system with inconsistent onboarding and no certification tracking.",
      },
      {
        title: "Solution and Success",
        img: SuccessImg,
        description:
          "DG FutureTech deployed a custom-built, integrated CRM and LMS in 3 months, kickoff to go-live  mapping the full client journey across all three product lines, giving real-time project visibility with custom pipeline reporting, and enabling structured onboarding with training/certification tracking, backed by clean data migration and zero disruption to live projects.",
      },
    ],
  },

  hrms: {
    label: "Integrated HRMS for a Growing Mid-Scale Organization",
    cards: [
      {
        title: "Client Background",
        img: userImg,
        description:
          "A growing mid-scale organization scaling headcount and operations, with HR processes not yet matched to that growth.",
      },
      {
        title: "Opportunity",
        img: OpportunitiesImg,
        description: "The organization sought to modernize its HR operations with a unified platform that automates key processes including onboarding, attendance, payroll, leave, and performance management while enhancing data security through role-based access and employee self-service, and providing real-time reporting and streamlined compliance without disrupting daily operations.",
      },
      {
        title: "Challenge",
        img: ChallengesImg,
        description: "Fragmented HR operations  manual records, outsourced payroll and attendance, partial performance tracking, and no role-based access  left sensitive data exposed and compliance reporting delayed.",
      },
      {
        title: "Solution and Success",
        img: SuccessImg,
        description:
          "DG FutureTech deployed a fully integrated HRMS, centralizing onboarding, attendance, payroll, leave, and performance management into one platform  with role-based access, automated compliance reporting, employee self-service, and real-time dashboards.",
      },
    ],
  },

  hims: {
    label: "Hospital Information Management System Implementation",
    cards: [
      {
        title: "Client Background",
        img: userImg,
        description:
          "A multi-specialty hospital was looking to modernize its clinical and administrative operations by replacing fragmented processes with a unified Hospital Information Management System (HIMS) to improve patient care and operational efficiency." 
      },
      {
        title: "Opportunity",
        img: OpportunitiesImg,
        description: "The hospital sought to digitize and integrate its operations through a centralized platform that streamlines patient records, appointments, billing, pharmacy, and inventory management, while improving interdepartmental coordination, enhancing patient experience, increasing operational efficiency, and ensuring regulatory compliance.",
      },
      {
        title: "Challenge",
        img: ChallengesImg,
        description: "As patient volumes and operational complexity increased, the hospital faced fragmented patient records, manual appointment scheduling and billing, limited inventory visibility, and disconnected departmental workflows that resulted in operational inefficiencies, revenue leakage.",
      },
      {
        title: "Solution and Success",
        img: SuccessImg,
        description:
          "DG FutureTech implemented a centralized Hospital Information Management System (HIMS) that unified patient records, appointments, billing, pharmacy, and inventory, automated clinical and administrative processes, improved collaboration across departments, and delivered real-time visibility, stronger compliance, enhanced patient experiences, and greater operational efficiency.",
      },
    ],
  },
};

// ─── Component ─────────────────────────────────────────────────────────────
const SuccessStory6 = () => {
  const { bannerTitle, bannerImage } = storyPageContent;
  const [activeCase, setActiveCase] = useState("rifab");

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.2, duration: 0.5 },
    }),
  };

  const highlightTerms = (text, terms) => {
    if (!terms || terms.length === 0) return text;
    const pattern = new RegExp(`(${terms.join("|")})`, "gi");
    const parts = text.split(pattern);
    return parts.map((part, idx) =>
      terms.some((t) => t.toLowerCase() === part.toLowerCase()) ? (
        <span key={idx} className={erpStyles.highlight}>{part}</span>
      ) : (
        part
      )
    );
  };

  const selected = caseStudies[activeCase];

  return (
    <div className="bg-gradient-to-b from-white to-gray-100 mb-5 mx-auto">
      <ServicesBanner title={bannerTitle} heroImage={bannerImage} description="Custom ERP Modules Implementation for Smarter Business Operations"/>

      {/* ── 3 Tab Links ── */}
      <div className="flex justify-center gap-4 mt-8 mb-6 flex-wrap px-4">
        {Object.entries(caseStudies).map(([key, study]) => (
          <button
            key={key}
            onClick={() => setActiveCase(key)}
            className={`px-6 py-2 rounded-full font-semibold text-sm transition-all duration-300 border
              ${activeCase === key
                ? "bg-blue-700 text-white border-blue-700 shadow-md"
                : "bg-white text-blue-700 border-blue-700 hover:bg-blue-50"
              }`}
          >
            {study.label}
          </button>
        ))}
      </div>

      {/* ── Case Study Content ── */}
      <div className="max-w-5xl mx-auto md:px-10 lg:px-10 lg:max-w-fit">
        {/* Heading */}
        <div className="flex justify-center items-center">
          <div className="m-5">
            <h1 className="text-3xl font-bold mt-2 text-center">
              {selected.heading}
            </h1>
          </div>
        </div>

        {/* Cards - re-animate when activeCase changes */}
        <div className="grid md:grid-cols-2 sm:grid-cols-1 gap-x-10 gap-y-10 w-full max-w-6xl mx-auto">
          {selected.cards.map((card, i) => (
            <motion.div
              key={`${activeCase}-${card.title}`}
              custom={i}
              initial="hidden"
              animate="visible"
              variants={cardVariants}
              className="bg-white rounded-2xl shadow-md hover:shadow-xl hover:shadow-blue-100 p-10 transition duration-300 ease-in-out"
            >
              <div className="flex items-center gap-4 mb-4">
                <img
                  src={card.img}
                  alt={card.title}
                  className="w-16 h-16 rounded-full border border-gray-200"
                />
                <h3 className="text-2xl font-semibold text-blue-700">
                  {card.title}
                </h3>
              </div>

              <p classname="text-gray-800 text-[16px] leading-[24px]">
                {highlightTerms(card.description, card.highlightWords)}
              </p>

              {card.subheading && (
                <p className="font-semibold text-gray-900 mt-4 mb-2">
                  {card.subheading}
                </p>
              )}

              {card.points && (
                <ul className="list-disc pl-5 space-y-2 mt-3">
                  {card.points.map((point, idx) => (
                    <li key={idx} className="text-gray-800 text-[16px] leading-[24px]">
                      {highlightTerms(point, card.highlightWords)}
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SuccessStory6;